import { useState } from "react";
import { Helmet } from "react-helmet-async";
import {
  Button,
  Container,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import AppUserSearchTable, {
  fetchAppUsersPage,
} from "src/components/users/AppUserSearchTable";

const EXPORT_LIMIT = 100;

const ACTIVITY_OPTIONS = [
  { value: "", label: "All activity" },
  { value: "active7", label: "Active in last 7 days" },
  { value: "active30", label: "Active in last 30 days" },
  { value: "inactive30", label: "Inactive for 30+ days" },
  { value: "never", label: "Never logged in" },
];

function csvValue(value) {
  const normalized = value === null || value === undefined ? "" : String(value);
  return `"${normalized.replace(/"/g, '""')}"`;
}

function downloadCsv(filename, rows) {
  const headers = [
    "Username",
    "EngageCraft ID",
    "Ref ID",
    "Country",
    "Favourite Club",
    "Created At",
    "Last Login At",
  ];
  const body = rows.map((row) =>
    [
      row.username,
      row.engagecraftId,
      row.refId,
      row.refCountryCode,
      row.refFavClubId,
      row.createdAt,
      row.lastLoginAt,
    ]
      .map(csvValue)
      .join(",")
  );
  const blob = new Blob(
    [[headers.map(csvValue).join(","), ...body].join("\n")],
    {
      type: "text/csv;charset=utf-8;",
    }
  );
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export default function UserSegmentationPage() {
  const [filters, setFilters] = useState({
    countryCode: "",
    activity: "",
    createdFrom: "",
    createdTo: "",
  });
  const [exporting, setExporting] = useState(false);

  const updateFilter = (key, value) =>
    setFilters((prev) => ({ ...prev, [key]: value }));

  const exportUsers = async () => {
    setExporting(true);
    try {
      const rows = [];
      let offset = 0;
      let total = 0;

      do {
        const payload = await fetchAppUsersPage({
          offset,
          limit: EXPORT_LIMIT,
          filters,
        });
        rows.push(...(payload.users ?? []));
        total = payload.total ?? rows.length;
        offset += EXPORT_LIMIT;
      } while (rows.length < total);

      downloadCsv(
        `users-export-${new Date().toISOString().slice(0, 10)}.csv`,
        rows
      );
    } finally {
      setExporting(false);
    }
  };

  const toolbar = (
    <Button variant="outlined" disabled={exporting} onClick={exportUsers}>
      {exporting ? "Exporting..." : "Export CSV"}
    </Button>
  );

  return (
    <>
      <Helmet>
        <title>User Segmentation / Export | DreamTeam Admin</title>
      </Helmet>

      <Container maxWidth="xl">
        <Stack spacing={3}>
          <Stack spacing={1}>
            <Typography variant="h4">User Segmentation / Export</Typography>
            <Typography variant="body2" color="text.secondary">
              Filter app users by registration and login signals, then export
              the matching segment.
            </Typography>
          </Stack>

          <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
            <TextField
              label="Country code"
              value={filters.countryCode}
              onChange={(event) =>
                updateFilter("countryCode", event.target.value.toUpperCase())
              }
              inputProps={{ maxLength: 3 }}
              sx={{ minWidth: 160 }}
            />
            <TextField
              select
              label="Activity"
              value={filters.activity}
              onChange={(event) => updateFilter("activity", event.target.value)}
              sx={{ minWidth: 220 }}
            >
              {ACTIVITY_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Created from"
              type="date"
              value={filters.createdFrom}
              onChange={(event) =>
                updateFilter("createdFrom", event.target.value)
              }
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Created to"
              type="date"
              value={filters.createdTo}
              onChange={(event) =>
                updateFilter("createdTo", event.target.value)
              }
              InputLabelProps={{ shrink: true }}
            />
          </Stack>

          <AppUserSearchTable filters={filters} toolbar={toolbar} />
        </Stack>
      </Container>
    </>
  );
}
